// React
import { useState, useEffect } from "react";
import type { FC } from "react";

// Zustand store
import { useTableStore } from "../../../store/oasis/tableStore";
import type { TablePaginationState } from "../../../store/oasis/tableStore";

// Librerias externas
import tw from "tailwind-styled-components";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  //   getSortedRowModel,
  //   getFilteredRowModel,
} from "@tanstack/react-table";

// Componentes
import ArrowButton from "../components/ArrowButton";

interface SimpleTableProps {
  data: any[];
  tableColumns: (...args: any[]) => any[];
  totalPages?: number;
  pagination?: number;
  nextPage?: () => void;
  previusPage?: () => void;
  pageRenderingType?: string;
  pageSize?: number;
  t: (key: string) => string;
  defaultText?: string;
  action1?: any;
  action2?: any;
  disabled?: boolean;
  environment?: string;
  itemsToDisplayInDevelopment?: string[];
  displayRange?: { min: number; max: number };
  isDarkMode?: boolean;
}

const SimpleTable: FC<SimpleTableProps> = ({
  data,
  tableColumns,
  totalPages = 0,
  pagination = 1,
  nextPage = () => {},
  previusPage = () => {},
  pageRenderingType = "",
  pageSize = 10,
  t,
  defaultText = "",
  action1,
  action2,
  disabled = false,
  environment = "",
  itemsToDisplayInDevelopment = [],
  displayRange = { min: 0, max: 0 },
  isDarkMode = false,
}) => {
  //   const [sorting, setSorting] = useState<any[]>([]);
  //   const [filtering, setFiltering] = useState("");

  // Estados globales
  const pageIndex = useTableStore(
    (state: TablePaginationState) => state.pageIndex
  );
  const pageSizeStore = useTableStore(
    (state: TablePaginationState) => state.pageSize
  );
  const setPageIndex = useTableStore(
    (state: TablePaginationState) => state.setPageIndex
  );
  const setPageSize = useTableStore(
    (state: TablePaginationState) => state.setPageSize
  );
  const setPagination = useTableStore(
    (state: TablePaginationState) => state.setPagination
  );
  // Adaptador para onPaginationChange de react-table
  const handlePaginationChange = (updaterOrValue: any) => {
    if (typeof updaterOrValue === "function") {
      // updaterOrValue es una función que recibe el estado anterior y retorna el nuevo
      setPagination(updaterOrValue({ pageIndex, pageSize: pageSizeStore }));
    } else {
      // updaterOrValue es el nuevo valor directamente
      setPagination(updaterOrValue);
    }
  };
  const reactTablePagination = { pageIndex, pageSize: pageSizeStore };

  // Estados locales
  const [copyData, setCopyData] = useState<any[]>([]);

  const shouldDisplayColumn = (colOrHeader: any) => {
    const colDef =
      colOrHeader.columnDef ?? colOrHeader.column?.columnDef ?? colOrHeader;
    if (!colDef?.meta?.displayValue) return true;
    const { min, max } = colDef.meta.displayValue;
    const { min: curMin, max: curMax } = displayRange;
    return !(curMax < min || curMin > max);
  };

  let processedColumns = tableColumns(t, action1, action2);
  processedColumns = processedColumns.filter(shouldDisplayColumn);
  processedColumns =
    environment !== "residenciales"
      ? processedColumns.filter(
          (elemento) =>
            !itemsToDisplayInDevelopment.includes(elemento.accessorKey)
        )
      : processedColumns;

  const tableOptions = {
    data: copyData,
    columns: processedColumns,
    getRowId: (row: any) => row.id,
    getCoreRowModel: getCoreRowModel(),
  };

  const table = useReactTable(
    pageRenderingType === "api"
      ? tableOptions
      : {
          ...tableOptions,
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: handlePaginationChange,
          state: {
            pagination: reactTablePagination,
          },
        }
  );

  const handleNextPage = () => {
    setPageIndex(pageIndex + 1);
  };

  const handlePreviousPage = () => {
    setPageIndex(pageIndex - 1);
  };

  useEffect(() => {
    (data && data.length) > 0 && setCopyData(data);
  }, [data]);

  useEffect(() => {
    setPageSize(pageSize ? pageSize : 10);
  }, [pageSize, setPageSize]);

  return (
    <TableContainer style={{ overflowX: "auto" }}>
      <Table
        className={isDarkMode ? "bg-[#232323]" : "bg-white"}
        style={{
          backgroundColor: isDarkMode ? "#232323" : "#fff",
        }}
      >
        <Thead
          className={
            isDarkMode ? "bg-[#0052A2] text-white" : "bg-[#1C93FF] text-white"
          }
          style={{
            backgroundColor: isDarkMode ? "#0052A2" : "#1C93FF",
            color: "white",
          }}
        >
          {table.getHeaderGroups().map((headerGroup: any) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => {
                if (!shouldDisplayColumn(header)) return null;
                const columnDef = header.column.columnDef;
                return (
                  <Th
                    key={header.id}
                    className={
                      isDarkMode
                        ? "bg-[#0052A2]  text-white"
                        : "bg-[#1C93FF] text-white"
                    }
                    style={{
                      width: columnDef.width,
                      textAlign:
                        typeof columnDef.headerTextAlign === "function"
                          ? columnDef.headerTextAlign(header)
                          : "left",
                      backgroundColor: isDarkMode ? "#0052A2" : "#1C93FF",
                      color: "white",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Th>
                );
              })}
            </tr>
          ))}
        </Thead>
        <tbody>
          {data?.length === 0 ? (
            <tr>
              <Td1
                colSpan={
                  processedColumns?.length || table.getAllLeafColumns().length
                }
                className={
                  isDarkMode
                    ? "bg-[#232323] text-white"
                    : "bg-white text-gray-900"
                }
                style={{
                  backgroundColor: isDarkMode ? "#232323" : "#fff",
                  color: isDarkMode ? "#fff" : "#222",
                }}
              >
                {defaultText}
              </Td1>
            </tr>
          ) : (
            table?.getRowModel()?.rows?.map((row: any, index: number) => (
              <tr
                key={row.id}
                style={{
                  backgroundColor: isDarkMode
                    ? index % 2 === 0
                      ? "#232323"
                      : "#1C1C1C"
                    : index % 2 === 0
                    ? "#f5f5f5"
                    : "white",
                }}
              >
                {row.getVisibleCells().map((cell: any) => {
                  if (!shouldDisplayColumn(cell.column)) return null;
                  return (
                    <Td2
                      key={cell.id}
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                      style={{
                        width: cell.column.columnDef.width,
                        height: cell.column.columnDef.height,
                        color: isDarkMode ? "#fff" : "#222",
                        textAlign:
                          typeof cell.column.columnDef.cellTextAlign ===
                          "function"
                            ? cell.column.columnDef.cellTextAlign(cell)
                            : "left",
                        fontWeight: cell.column.columnDef.fontWeight,
                        backgroundColor: isDarkMode
                          ? index % 2 === 0
                            ? "#232323"
                            : "#1C1C1C"
                          : index % 2 === 0
                          ? "#f5f5f5"
                          : "white",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td2>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      {(pageRenderingType === "api" ? totalPages > 0 : data?.length > 0) && (
        <PageButtonsContainer className={isDarkMode ? "" : "bg-white"}>
          <ArrowButton
            buttonName="arrowLeft"
            action={
              pageRenderingType === "api"
                ? () => previusPage()
                : () => handlePreviousPage()
            }
            disabled={
              pageRenderingType === "api"
                ? pagination === 1
                : reactTablePagination.pageIndex === 0
            }
          />
          <PaginationView
            className={isDarkMode ? "text-white" : "text-gray-800"}
            style={{
              color: isDarkMode ? "#fff" : "#222",
              backgroundColor: isDarkMode ? "#232323" : "#fff",
              borderRadius: "6px",
              padding: "4px 12px",
            }}
          >
            {`${t("page")} ${
              pageRenderingType === "api"
                ? pagination
                : reactTablePagination.pageIndex + 1
            } ${t("of")} ${
              pageRenderingType === "api"
                ? totalPages
                : table.getPageCount().toLocaleString()
            }`}
          </PaginationView>
          <ArrowButton
            buttonName="arrowRight"
            action={
              pageRenderingType === "api"
                ? () => nextPage()
                : () => handleNextPage()
            }
            disabled={
              disabled ||
              (pageRenderingType === "api"
                ? pagination >= totalPages
                : reactTablePagination.pageIndex >= table.getPageCount() - 1)
            }
          />
        </PageButtonsContainer>
      )}
    </TableContainer>
  );
};

export default SimpleTable;

const TableContainer = tw.div`
w-full flex flex-col 
gap-4 justify-center items-center
`;

const Table = tw.table`
w-full rounded-xl overflow-hidden
shadow-gray-300 shadow-custom
`;

const Thead = tw.thead`
bg-[#0052A2] h-auto rounded-xl
text-white text-xs font-bold
`;

const Th = tw.th`
text-center p-3
${(props) => props.className} 
`;

const Td1 = tw.td`
text-xs p-4 text-center
`;

const Td2 = tw.td`
text-xs p-2 w-auto
whitespace-pre-line 
${(props) => props.className} 
`;

const PageButtonsContainer = tw.div`
w-fit flex p-2 gap-2 justify-center items-center
`;

const PaginationView = tw.span`
text-sm font-Roboto text-gray-800
`;
